export const AccountTriageContent = {
  breadcrumb: 'Account {{number}}',
  pageTitle: 'Now tell us about the {{ordinal}} account you’re inheriting',
  continueButton: 'Continue',
  transferAnotherAccountButton: 'Transfer another account',
  reviewAndSignButton: 'Review and sign',
  dropdownPlaceholderText: 'Please Select One',
  genericError: 'This field is required',
  dropdowns: {
    accountType: {
      question: `What type of account are you inheriting?`,
      label: 'Account type',
    },
    relationship: {
      question: `How were you named or listed on the account?`,
      label: 'Relationship',
    },
    isOnlyOneBeneficiary: {
      question: `Are you the only listed beneficiary?`,
      label: `Sole beneficiary`,
    },
  },
  isMoreThanOneInheritingAccountCheckbox: `I'm inheriting more than one {{type}} account`,
  isOnlyOneChildCheckbox: `I'm the only child/grandchild of the account owner`,
  tooltips: {
    accountType:
      'We recommend taking a look at a previous statement for the person who passed away. You can find the account types there.',
    relationship:
      'The person who passed away managed the beneficiaries who will receive the assets. They did this by listing their name, relationship, or an executor. If they did not designate beneficiaries their assets will go to probate, and a court will determine an executor.',
  },
  jointAccounts: {
    title: `Select the joint account(s) you\'d like to inherit`,
    errorMessage: `Make a selection to continue`,
    banner: {
      title: `We couldn\'t find a joint account for you to inherit`,
      line1: `We currently only support inheritance transfers for joint accounts with two owners registered as Joint tenants with rights of survivorship (JTWROS). If you’re looking to inherit a joint account with another registration type, please give us a call at 877-662-7447.`,
      line2: `If you have other accounts to inherit, select another account type in the dropdown and continue, or review and sign if you’re ready to submit the rest of your request.`,
    },
  },
  receivingAccount: {
    title: `Where would you like to transfer the investments?`,
    errorMessage: 'Make a selection to continue',
    ira: {
      description: `You can open a <em>new</em> IRA with us, or you can move the money to an <em>eligible</em> IRA you already have open with us—whichever you prefer.`,
      newHeader: `Open a new IRA`,
      newRadioText: `I prefer to open a new IRA brokerage account`,
      existingHeader: `Select an eligible IRA`,
    },
    nonRetirement: {
      description: `You can open a <em>new</em> individual nonretirement account with us, or you can move the money to an <em>eligible</em> individual nonretirement account you already have open with us—whichever you prefer.`,
      newHeader: `Open a new account`,
      newRadioText: `I prefer to open a new brokerage account`,
      existingHeader: `Select an eligible nonretirement account`,
    },
  },
  accountTriageQA: [
    {
      title: `What does “per stirpes” mean?`,
      content: `In estate planning, 'per stirpes' means that if a named beneficiary dies before inheriting, their share is divided equally among their heirs. This ensures that the inheritance stays within that family branch.`,
    },
    {
      title: `What if there are multiple beneficiaries?`,
      content: `Each beneficiary will have to complete this process to inherit the assets.`,
    },
    {
      title: `What if I’m not sure how I’m listed?`,
      content: `If you’re unsure about how you’re listed on an account, call us at 877-662-7447 so we can verify your relationship. `,
    },
  ],
  assumeOrInheritTitle: 'Do you want to assume or inherit the account?',
  assumeOrInheritText: `As a spouse and sole beneficiary, you have the option to assume the decedent’s IRA and treat it as your own.`,
  assumeOrInheritLink: 'Learn more about assuming vs. inheriting',
  assumeOrInheritOptions: [
    {
      title: 'Assume',
      description: `Transfers into your name`,
      value: 'assume',
    },
    {
      title: 'Inherit',
      description: `Transfers into an Inherited Roth IRA`,
      value: 'inherit',
    },
  ],
  assumeInheritError: 'Make a selection to continue',
  assumeOrInheritModal: {
    ROTH_IRA: {
      title: 'Assuming vs. inheriting a Roth IRA',
      description:
        'As a spouse and sole beneficiary, you have the option to assume the decedent’s IRA and treat it as your own. Please note, if you choose to inherit the account now, you can still assume it later.',
      assume: {
        title: 'Assume the Roth IRA',
        subTitle: 'Transfers into your name',
        contributionsTerm: 'You <b>can</b> contribute the maximum amount each year.',
        contributionsTitle: 'Contributions',
        distributions: [
          'Distributions can be taken out at any time, penalty-free, if the 5-year holding period for Roth IRAs was met and the decedent was at least 59.5 years old.',
          'If the account has not met the 5-year holding period, withdrawals of earnings may be subject to income tax.',
        ],
        distributionsTitle: 'Distributions',
        rmd: 'There are no RMD requirements for an assumed Roth IRA.',
        rmdTitle: 'Required Minimum Distribution (RMD)',
      },
      inherit: {
        title: 'Inherit the Roth IRA',
        subTitle: 'Transfers into an inherited Roth IRA',
        contributionsTerm: 'You <b>can’t</b> contribute to an inherited Roth IRA.',
        contributionsTitle: 'Contributions',
        distributions: [
          'Distributions can be taken out at any time, penalty-free, if the 5-year holding period for Roth IRAs was met and the decedent was at least 59.5 years old.',
          'If the account has not met the 5-year holding period, withdrawals of earnings may be subject to income tax.',
        ],
        distributionsTitle: 'Distributions',
        rmd: 'RMD requirements vary based on the decedent’s age and year of passing. Please visit the IRS website to learn more about ',
        rmdTitle: 'Required Minimum Distribution (RMD)',
        rmdLinkUrl:
          'https://investor.vanguard.com/investor-resources-education/retirement/rmd-rules-for-inherited-iras',
        rmdLinkLabel: 'RMD requirements for Roth IRAs.',
      },
    },
    TRADITIONAL_IRA: {
      title: 'Assuming vs. inheriting an IRA',
      description:
        'As a spouse and sole beneficiary, you have the option to assume the decedent’s IRA and treat it as your own. Please note, if you choose to inherit the account now, you can still assume it later.',
      assume: {
        title: 'Assume the IRA',
        subTitle: 'Transfers into your name',
        contributionsTerm: 'You <b>can</b> contribute the maximum amount each year.',
        contributionsTitle: 'Contributions',
        distributions: [
          'Any distributions taken before age 59.5 will be included in your taxable income and may be subject to an additional 10% tax.',
          'Once you reach age 59.5, penalty-free distributions can be taken at anytime.',
        ],
        distributionsTitle: 'Distributions',
        rmd: "You'll start taking RMDs at age 73.",
        rmdTitle: 'Required Minimum Distribution (RMD)',
      },
      inherit: {
        title: 'Inherit the IRA',
        subTitle: 'Transfers into an inherited IRA',
        contributionsTerm: 'You <b>can’t</b> contribute to an inherited IRA.',
        contributionsTitle: 'Contributions',
        distributions: [
          "Generally you can choose to start taking distributions December 31 of the year after the decedent's passing, or the year the decedent would've turned 73.",
          "You'll also choose to take the distributions based off your own life expectancy or by using the 5 or 10-year rule.",
        ],
        distributionsTitle: 'Distributions',
        rmd: 'RMD requirements vary based on the decedent’s age and year of passing. Please visit the IRS website to learn more about ',
        rmdTitle: 'Required Minimum Distribution (RMD)',
        rmdLinkUrl:
          'https://investor.vanguard.com/investor-resources-education/retirement/rmd-rules-for-inherited-iras',
        rmdLinkLabel: 'RMD requirements for IRAs.',
      },
    },
  },
  beneficiaryCard: {
    cardTitle: 'Beneficiary',
    decedentBaseInfoButtonText: 'Save beneficiary information',
    addDecedentBaseInfoButtonText: 'Add another beneficiary',
    title: 'List all the other beneficiaries',
    description: `We'll need the names and birthdates for all the account owner's children (except you).`,
    reminderTitle: `Important reminder: `,
    reminderdescription: `Each beneficiary who you list here will also need to complete this same online process. To make everything as easy as possible for you, we recommend making sure all names and birthdates are exactly the same on all submissions (for example, listing John J. Doe and not simply John Doe).`,
    fullLegalName: {
      reqErrorMsg: 'This field is required',
      label: 'Full legal name',
    },
    dateOfBirth: {
      reqErrorMsg: 'This field is required',
      label: 'Date of birth',
    },
    dateOfDeath: {
      doDbeforeDobErrorMsg: 'The date of death cannot be before the date of birth',
      label: 'Date of death',
    },
    notSavedErrorMsg: 'Save information entered',
    removeLabel: 'Remove',
    editLabel: 'Edit',
    hasChildrendQuestion: `Does this person who passed away have natural or legally adopted children?`,
    reqErrorMsg: 'This field is required',
  },
};
